import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { capitalizeFirst } from './utils';
import './AccountDisplayNameManagement.css';

function AccountDisplayNameManagement({ onClose }) {
  const [activeTab, setActiveTab] = useState('accounts'); // 'accounts', 'notifications', 'access', 'frontend'
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAccounts, setEditingAccounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const [coveredCallDeltaAlert, setCoveredCallDeltaAlert] = useState('');
  const [sentBetweenStart, setSentBetweenStart] = useState('');
  const [sentBetweenEnd, setSentBetweenEnd] = useState('');
  const [dontResendForHours, setDontResendForHours] = useState('');
  const [defaultConidGrouped, setDefaultConidGrouped] = useState(true);
  const [enableSidebar, setEnableSidebar] = useState(false);
  const [showMargin, setShowMargin] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [grants, setGrants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserForGrant, setSelectedUserForGrant] = useState('');
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [weeklyGoal, setWeeklyGoal] = useState('');
  const [monthlyGoal, setMonthlyGoal] = useState('');
  const [monthlyDrawdownLimit, setMonthlyDrawdownLimit] = useState('');
  const [yearlyGoal, setYearlyGoal] = useState('');
  const [yearlyGoalPercentage, setYearlyGoalPercentage] = useState('');
  const [yearlyGoalType, setYearlyGoalType] = useState('amount');
  const [yearlyStartEquity, setYearlyStartEquity] = useState('');
  // Minimum and Stretch goals
  const [yearlyMinimumGoal, setYearlyMinimumGoal] = useState('');
  const [yearlyMinimumGoalPercentage, setYearlyMinimumGoalPercentage] = useState('');
  const [yearlyMinimumGoalType, setYearlyMinimumGoalType] = useState('amount');
  const [yearlyStretchGoal, setYearlyStretchGoal] = useState('');
  const [yearlyStretchGoalPercentage, setYearlyStretchGoalPercentage] = useState('');
  const [yearlyStretchGoalType, setYearlyStretchGoalType] = useState('amount');
  const [equityAdjustment, setEquityAdjustment] = useState('');

  useEffect(() => {
    fetchAccounts();
    fetchUserSettings();
    fetchGrants();
    fetchAllUsers();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      const accountsArray = Array.isArray(accountsData) ? accountsData : [];
      setAccounts(accountsArray);
      
      // Initialize editing state
      const editingState = {};
      accountsArray.forEach(account => {
        editingState[account.id] = account.display_name || '';
      });
      setEditingAccounts(editingState);
    } catch (err) {
      setError('Error fetching accounts: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (accountId, value) => {
    setEditingAccounts(prev => ({
      ...prev,
      [accountId]: value,
    }));
  };

  const handleSave = async (accountId) => {
    try {
      setSaving(true);
      setError(null);
      const displayName = editingAccounts[accountId] || '';
      await axios.patch(`${API_URL}/portfolio/accounts/${accountId}/`, {
        display_name: displayName,
      });
      // Update the account in the list
      setAccounts(prev => prev.map(acc => 
        acc.id === accountId 
          ? { ...acc, display_name: displayName }
          : acc
      ));
    } catch (err) {
      setError('Error saving account: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError(null);
      const promises = accounts.map(account => 
        axios.patch(`${API_URL}/portfolio/accounts/${account.id}/`, {
          display_name: editingAccounts[account.id] || '',
        })
      );
      await Promise.all(promises);
      await fetchAccounts();
    } catch (err) {
      setError('Error saving accounts: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      // Handle both list and single object responses
      let settings = null;
      if (response.data.results && Array.isArray(response.data.results)) {
        settings = response.data.results[0] || null;
      } else if (Array.isArray(response.data)) {
        settings = response.data[0] || null;
      } else {
        settings = response.data || null;
      }
      
      if (settings) {
        setUserSettings(settings);
        setCoveredCallDeltaAlert(settings.covered_call_delta_alert !== null && settings.covered_call_delta_alert !== undefined 
          ? settings.covered_call_delta_alert.toString() 
          : '');
        // Convert time fields from "HH:MM:SS" to "HH:MM" for input
        setSentBetweenStart(settings.sent_between_start ? settings.sent_between_start.substring(0, 5) : '');
        setSentBetweenEnd(settings.sent_between_end ? settings.sent_between_end.substring(0, 5) : '');
        setDontResendForHours(settings.dont_resend_for_hours !== null && settings.dont_resend_for_hours !== undefined 
          ? settings.dont_resend_for_hours.toString() 
          : '');
        setDefaultConidGrouped(settings.default_conid_grouped !== null && settings.default_conid_grouped !== undefined 
          ? settings.default_conid_grouped 
          : true);
        setEnableSidebar(settings.enable_sidebar !== null && settings.enable_sidebar !== undefined
          ? settings.enable_sidebar
          : false);
        setShowMargin(settings.show_margin !== null && settings.show_margin !== undefined
          ? settings.show_margin
          : false);
        setWeeklyGoal(settings.weekly_pnl_goal !== null && settings.weekly_pnl_goal !== undefined 
          ? settings.weekly_pnl_goal.toString() 
          : '');
        setMonthlyGoal(settings.monthly_pnl_goal !== null && settings.monthly_pnl_goal !== undefined 
          ? settings.monthly_pnl_goal.toString() 
          : '');
        setMonthlyDrawdownLimit(settings.monthly_drawdown_limit_pct !== null && settings.monthly_drawdown_limit_pct !== undefined
          ? settings.monthly_drawdown_limit_pct.toString()
          : '');
        setYearlyGoal(settings.yearly_pnl_goal !== null && settings.yearly_pnl_goal !== undefined 
          ? settings.yearly_pnl_goal.toString() 
          : '');
        setYearlyGoalPercentage(settings.yearly_pnl_goal_percentage !== null && settings.yearly_pnl_goal_percentage !== undefined 
          ? settings.yearly_pnl_goal_percentage.toString() 
          : '');
        setYearlyGoalType(settings.yearly_goal_type || 'amount');
        setYearlyStartEquity(settings.yearly_start_equity !== null && settings.yearly_start_equity !== undefined 
          ? settings.yearly_start_equity.toString() 
          : '');
        // Minimum goal
        setYearlyMinimumGoal(settings.yearly_minimum_goal !== null && settings.yearly_minimum_goal !== undefined 
          ? settings.yearly_minimum_goal.toString() 
          : '');
        setYearlyMinimumGoalPercentage(settings.yearly_minimum_goal_percentage !== null && settings.yearly_minimum_goal_percentage !== undefined 
          ? settings.yearly_minimum_goal_percentage.toString() 
          : '');
        setYearlyMinimumGoalType(settings.yearly_minimum_goal_type || 'amount');
        // Stretch goal
        setYearlyStretchGoal(settings.yearly_stretch_goal !== null && settings.yearly_stretch_goal !== undefined 
          ? settings.yearly_stretch_goal.toString() 
          : '');
        setYearlyStretchGoalPercentage(settings.yearly_stretch_goal_percentage !== null && settings.yearly_stretch_goal_percentage !== undefined 
          ? settings.yearly_stretch_goal_percentage.toString() 
          : '');
        setYearlyStretchGoalType(settings.yearly_stretch_goal_type || 'amount');
        setEquityAdjustment(settings.equity_adjustment !== null && settings.equity_adjustment !== undefined 
          ? settings.equity_adjustment.toString() 
          : '0');
      } else {
        // No settings exist yet, set to null
        setUserSettings(null);
        setCoveredCallDeltaAlert('');
        setSentBetweenStart('');
        setSentBetweenEnd('');
        setDontResendForHours('');
        setDefaultConidGrouped(true);
        setShowMargin(false);
        setWeeklyGoal('');
        setMonthlyGoal('');
        setYearlyGoal('');
        setYearlyGoalPercentage('');
        setYearlyGoalType('amount');
        setYearlyStartEquity('');
        setYearlyMinimumGoal('');
        setYearlyMinimumGoalPercentage('');
        setYearlyMinimumGoalType('amount');
        setYearlyStretchGoal('');
        setYearlyStretchGoalPercentage('');
        setYearlyStretchGoalType('amount');
      }
    } catch (err) {
      console.error('Error fetching user settings:', err);
      setUserSettings(null);
      setCoveredCallDeltaAlert('');
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setError(null);
      
      // Validate delta alert
      const deltaValue = coveredCallDeltaAlert === '' ? null : parseFloat(coveredCallDeltaAlert);
      if (isNaN(deltaValue) && coveredCallDeltaAlert !== '') {
        setError('Please enter a valid number for Covered Call Delta Alerts');
        return;
      }
      
      // Validate hours
      const hoursValue = dontResendForHours === '' ? null : parseInt(dontResendForHours);
      if (isNaN(hoursValue) && dontResendForHours !== '') {
        setError('Please enter a valid number for "Don\'t resend for hours"');
        return;
      }
      
      // Convert time strings to "HH:MM:SS" format
      const startTime = sentBetweenStart ? `${sentBetweenStart}:00` : null;
      const endTime = sentBetweenEnd ? `${sentBetweenEnd}:00` : null;
      
      const settingsData = {
        covered_call_delta_alert: deltaValue,
        sent_between_start: startTime,
        sent_between_end: endTime,
        dont_resend_for_hours: hoursValue,
      };
      
      if (userSettings && userSettings.id) {
        // Update existing settings
        await axios.patch(`${API_URL}/portfolio/user-settings/${userSettings.id}/`, settingsData);
      } else {
        // Create new settings if they don't exist
        const response = await axios.post(`${API_URL}/portfolio/user-settings/`, settingsData);
        setUserSettings(response.data);
      }
      await fetchUserSettings();
    } catch (err) {
      setError('Error saving settings: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveFrontendSettings = async () => {
    try {
      setSavingSettings(true);
      setError(null);
      
      if (userSettings && userSettings.id) {
        // Update existing settings
        await axios.patch(`${API_URL}/portfolio/user-settings/${userSettings.id}/`, {
          default_conid_grouped: defaultConidGrouped,
          enable_sidebar: enableSidebar,
          show_margin: showMargin,
        });
      } else {
        // Create new settings if they don't exist
        const response = await axios.post(`${API_URL}/portfolio/user-settings/`, {
          default_conid_grouped: defaultConidGrouped,
          enable_sidebar: enableSidebar,
          show_margin: showMargin,
        });
        setUserSettings(response.data);
      }
      await fetchUserSettings();
    } catch (err) {
      setError('Error saving frontend settings: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveGoals = async () => {
    try {
      setSavingSettings(true);
      setError(null);
      const goalsData = {
        weekly_pnl_goal: weeklyGoal ? parseFloat(weeklyGoal) : null,
        monthly_pnl_goal: monthlyGoal ? parseFloat(monthlyGoal) : null,
        monthly_drawdown_limit_pct: monthlyDrawdownLimit ? parseFloat(monthlyDrawdownLimit) : null,
        yearly_pnl_goal: yearlyGoalType === 'amount' && yearlyGoal ? parseFloat(yearlyGoal) : null,
        yearly_pnl_goal_percentage: yearlyGoalType === 'percentage' && yearlyGoalPercentage ? parseFloat(yearlyGoalPercentage) : null,
        yearly_goal_type: yearlyGoalType,
        yearly_start_equity: yearlyStartEquity ? parseFloat(yearlyStartEquity) : null,
        yearly_minimum_goal: yearlyMinimumGoalType === 'amount' && yearlyMinimumGoal ? parseFloat(yearlyMinimumGoal) : null,
        yearly_minimum_goal_percentage: yearlyMinimumGoalType === 'percentage' && yearlyMinimumGoalPercentage ? parseFloat(yearlyMinimumGoalPercentage) : null,
        yearly_minimum_goal_type: yearlyMinimumGoalType,
        yearly_stretch_goal: yearlyStretchGoalType === 'amount' && yearlyStretchGoal ? parseFloat(yearlyStretchGoal) : null,
        yearly_stretch_goal_percentage: yearlyStretchGoalType === 'percentage' && yearlyStretchGoalPercentage ? parseFloat(yearlyStretchGoalPercentage) : null,
        yearly_stretch_goal_type: yearlyStretchGoalType,
        equity_adjustment: equityAdjustment ? parseFloat(equityAdjustment) : 0,
      };
      
      if (userSettings && userSettings.id) {
        // Update existing settings
        await axios.patch(`${API_URL}/portfolio/user-settings/${userSettings.id}/`, goalsData);
      } else {
        // Create new settings if they don't exist
        const response = await axios.post(`${API_URL}/portfolio/user-settings/`, goalsData);
        setUserSettings(response.data);
      }
      await fetchUserSettings();
      alert('Goals saved successfully! The Equity page will refresh automatically when you return to it.');
    } catch (err) {
      setError('Error saving goals: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAutoFillYearStartEquity = async () => {
    try {
      // Get equity data for January 1st of current year
      const currentYear = new Date().getFullYear();
      const jan1Date = `01/01/${String(currentYear).slice(-2)}`;
      
      const response = await axios.get(`${API_URL}/portfolio/equity/`);
      const equityData = response.data.results || response.data;
      const equityArray = Array.isArray(equityData) ? equityData : [];
      
      // Find equity record for January 1st
      const jan1Equity = equityArray.find(e => e.report_date === jan1Date);
      
      if (jan1Equity && jan1Equity.total) {
        setYearlyStartEquity(parseFloat(jan1Equity.total).toFixed(2));
      } else {
        // If not found, try to find the first equity record of the year
        const yearEquity = equityArray.find(e => {
          if (!e.report_date) return false;
          const parts = e.report_date.split('/');
          if (parts.length === 3) {
            const year = 2000 + parseInt(parts[2], 10);
            return year === currentYear;
          }
          return false;
        });
        
        if (yearEquity && yearEquity.total) {
          setYearlyStartEquity(parseFloat(yearEquity.total).toFixed(2));
        } else {
          alert('Could not find equity data for January 1st. Please enter manually.');
        }
      }
    } catch (err) {
      console.error('Error fetching equity for year start:', err);
      alert('Error fetching equity data. Please enter manually.');
    }
  };

  const fetchGrants = async () => {
    try {
      setLoadingGrants(true);
      const response = await axios.get(`${API_URL}/portfolio/user-access-grants/?page_size=all`);
      const grantsData = response.data.results || response.data;
      const grantsArray = Array.isArray(grantsData) ? grantsData : [];
      setGrants(grantsArray);
    } catch (err) {
      console.error('Error fetching grants:', err);
      setGrants([]);
    } finally {
      setLoadingGrants(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Fetch all users (you might want to add a dedicated endpoint for this)
      // For now, we'll get users from grants or create a simple endpoint
      const response = await axios.get(`${API_URL}/portfolio/user-access-grants/available-users/`);
      const users = response.data || [];
      setAllUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
      // Fallback: try to get users from Django admin or create a simple list
      // For now, we'll handle this gracefully
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedUserForGrant) {
      setError('Please enter a username');
      return;
    }

    try {
      setLoadingGrants(true);
      setError(null);
      await axios.post(`${API_URL}/portfolio/user-access-grants/`, {
        grantee_username: selectedUserForGrant.trim(),
        is_active: true
      });
      setSelectedUserForGrant('');
      await fetchGrants();
    } catch (err) {
      setError('Error granting access: ' + (err.response?.data?.detail || err.response?.data?.grantee_username?.[0] || err.message));
      console.error(err);
    } finally {
      setLoadingGrants(false);
    }
  };

  const handleRevokeAccess = async (grantId) => {
    try {
      setLoadingGrants(true);
      setError(null);
      await axios.patch(`${API_URL}/portfolio/user-access-grants/${grantId}/`, {
        is_active: false
      });
      await fetchGrants();
    } catch (err) {
      setError('Error revoking access: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setLoadingGrants(false);
    }
  };

  // Get list of users who can be granted access (exclude current user and already granted users)
  const getAvailableUsersForGrant = () => {
    // This would ideally come from an API endpoint that lists all users
    // For now, we'll need to create a simple endpoint or use a different approach
    // Let's add a simple user list endpoint or use the grants to infer users
    return [];
  };

  const currentUsername = localStorage.getItem('api_username');
  const grantsGiven = grants.filter(g => g.grantor_username === currentUsername);
  const grantsReceived = grants.filter(g => g.grantee_username === currentUsername && g.is_active);

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="back-button" onClick={onClose}>← Back</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Tabs Navigation */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            Accounts
          </button>
          <button
            className={`settings-tab ${activeTab === 'access' ? 'active' : ''}`}
            onClick={() => setActiveTab('access')}
          >
            Access Control
          </button>
          <button
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
          </button>
          <button
            className={`settings-tab ${activeTab === 'frontend' ? 'active' : ''}`}
            onClick={() => setActiveTab('frontend')}
          >
            Frontend Settings
          </button>
          <button
            className={`settings-tab ${activeTab === 'goals' ? 'active' : ''}`}
            onClick={() => setActiveTab('goals')}
          >
            Goals
          </button>
        </div>

        <div className="account-management-content">
          {/* Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="settings-tab-content">
              {loading ? (
                <div className="loading">Loading accounts...</div>
              ) : accounts.length === 0 ? (
                <div className="empty-state">No accounts found.</div>
              ) : (
                <div className="account-list">
                  {accounts.map((account) => (
                    <div key={account.id} className="account-item">
                      <div className="account-info">
                        <div className="account-id">{account.broker_account_id}</div>
                        {account.display_name && (
                          <div className="account-current-name">Current: {account.display_name}</div>
                        )}
                      </div>
                      <div className="account-input-group">
                        <input
                          type="text"
                          value={editingAccounts[account.id] || ''}
                          onChange={(e) => handleInputChange(account.id, e.target.value)}
                          placeholder="Enter display name..."
                          maxLength={255}
                          className="account-input"
                        />
                        <button
                          className="btn-save"
                          onClick={() => handleSave(account.id)}
                          disabled={saving}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Access Control Tab */}
          {activeTab === 'access' && (
            <div className="settings-tab-content">
              <div className="settings-section-description">
                Grant other users access to view your data in read-only mode.
              </div>
              
              {/* Grant Access Form */}
              <div className="settings-item">
                <label className="settings-label">
                  Grant access to user:
                </label>
                <div className="settings-input-group">
                  <input
                    type="text"
                    value={selectedUserForGrant}
                    onChange={(e) => setSelectedUserForGrant(e.target.value)}
                    placeholder="Enter username..."
                    className="settings-input"
                  />
                  <button
                    className="btn-save"
                    onClick={handleGrantAccess}
                    disabled={loadingGrants || !selectedUserForGrant}
                  >
                    {loadingGrants ? 'Granting...' : 'Grant Access'}
                  </button>
                </div>
              </div>

              {/* Grants Given */}
              <div className="grants-section">
                <h4 className="grants-section-title">Users with access to your data</h4>
                {loadingGrants ? (
                  <div className="loading">Loading...</div>
                ) : grantsGiven.length === 0 ? (
                  <div className="empty-state">No access grants given yet.</div>
                ) : (
                  <div className="grants-list">
                    {grantsGiven.map((grant) => (
                      <div key={grant.id} className="grant-item">
                        <div className="grant-info">
                          <span className="grant-username">{capitalizeFirst(grant.grantee_username)}</span>
                          <span className="grant-date">
                            {new Date(grant.created_at).toLocaleDateString()}
                          </span>
                          <span className={`grant-status ${grant.is_active ? 'active' : 'inactive'}`}>
                            {grant.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {grant.is_active && (
                          <button
                            className="btn-revoke"
                            onClick={() => handleRevokeAccess(grant.id)}
                            disabled={loadingGrants}
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Grants Received */}
              {grantsReceived.length > 0 && (
                <div className="grants-section">
                  <h4 className="grants-section-title">Users who granted you access</h4>
                  <div className="grants-list">
                    {grantsReceived.map((grant) => (
                      <div key={grant.id} className="grant-item">
                        <div className="grant-info">
                          <span className="grant-username">{capitalizeFirst(grant.grantor_username)}</span>
                          <span className="grant-date">
                            {new Date(grant.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Frontend Settings Tab */}
          {activeTab === 'frontend' && (
            <div className="settings-tab-content">
              <div className="settings-section-description">
                Configure default frontend display settings.
              </div>
              
              {/* Default ConID Grouped Setting */}
              <div className="settings-item">
                <label className="settings-label">
                  Default ConID Grouped:
                </label>
                <div className="settings-input-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={defaultConidGrouped}
                      onChange={(e) => setDefaultConidGrouped(e.target.checked)}
                      className="settings-checkbox"
                    />
                    <span>Group trades by CONID by default</span>
                  </label>
                </div>
                <div className="settings-help-text">
                  When enabled, trades will be grouped by CONID by default on the Trades page.
                </div>
              </div>

              {/* Enable Sidebar Setting */}
              <div className="settings-item">
                <label className="settings-label">
                  Enable Sidebar:
                </label>
                <div className="settings-input-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={enableSidebar}
                      onChange={(e) => setEnableSidebar(e.target.checked)}
                      className="settings-checkbox"
                    />
                    <span>Show sidebar navigation panel</span>
                  </label>
                  <button
                    className="btn-save"
                    onClick={handleSaveFrontendSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <div className="settings-help-text">
                  When enabled, a sidebar navigation panel will be displayed on the left side of the screen for quick access to key features and information.
                </div>
              </div>

              {/* Show Margin Setting */}
              <div className="settings-item">
                <label className="settings-label">
                  Show Margin:
                </label>
                <div className="settings-input-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showMargin}
                      onChange={(e) => setShowMargin(e.target.checked)}
                      className="settings-checkbox"
                    />
                    <span>Show Margin submenu under Equity</span>
                  </label>
                  <button
                    className="btn-save"
                    onClick={handleSaveFrontendSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <div className="settings-help-text">
                  When enabled, an extra "Margin" tab appears under the Equity menu showing the last known net liquidation, margin, buying power and excess liquidity numbers fetched from IBKR (updated hourly while TWS is connected).
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-tab-content">
              <div className="settings-item">
                <label className="settings-label">
                  Telegram meldingen bij een delta hoger of gelijk aan:
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.01"
                    value={coveredCallDeltaAlert}
                    onChange={(e) => setCoveredCallDeltaAlert(e.target.value)}
                    placeholder="Enter delta threshold..."
                    className="settings-input"
                  />
                </div>
                {userSettings && userSettings.covered_call_delta_alert !== null && userSettings.covered_call_delta_alert !== undefined && (
                  <div className="settings-current-value">
                    Current: {userSettings.covered_call_delta_alert}
                  </div>
                )}
              </div>

              <div className="settings-item">
                <label className="settings-label">
                  Verstuur alerts tussen:
                </label>
                <div className="settings-input-group">
                  <input
                    type="time"
                    value={sentBetweenStart}
                    onChange={(e) => setSentBetweenStart(e.target.value)}
                    className="settings-input"
                    style={{ width: '150px' }}
                  />
                  <span style={{ margin: '0 10px' }}>en</span>
                  <input
                    type="time"
                    value={sentBetweenEnd}
                    onChange={(e) => setSentBetweenEnd(e.target.value)}
                    className="settings-input"
                    style={{ width: '150px' }}
                  />
                </div>
                <div className="settings-help-text">
                  Alerts worden alleen verstuurd tussen deze tijden (bijv. 09:00 - 17:00)
                </div>
              </div>

              <div className="settings-item">
                <label className="settings-label">
                  Stuur geen nieuwe alert voor (uren):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={dontResendForHours}
                    onChange={(e) => setDontResendForHours(e.target.value)}
                    placeholder="Bijv. 4"
                    className="settings-input"
                  />
                </div>
                <div className="settings-help-text">
                  Na het versturen van een alert, wacht dit aantal uren voordat een nieuwe alert voor dezelfde optie wordt verstuurd
                </div>
              </div>

              <div className="settings-item">
                <button
                  className="btn-save"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  style={{ marginTop: '1rem' }}
                >
                  {savingSettings ? 'Saving...' : 'Save All Settings'}
                </button>
              </div>
            </div>
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && (
            <div className="settings-tab-content">
              <div className="settings-section-description">
                Set your P&L goals for weekly, monthly, and yearly targets. Goals are used to track your progress in the Equity overview.
              </div>
              
              <div className="settings-item">
                <label className="settings-label">
                  Weekly P&L Goal (EUR):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.01"
                    value={weeklyGoal}
                    onChange={(e) => setWeeklyGoal(e.target.value)}
                    placeholder="e.g. 100.00"
                    className="settings-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="settings-help-text">
                  Target P&L per week (Monday to Friday)
                </div>
              </div>

              <div className="settings-item">
                <label className="settings-label">
                  Monthly P&L Goal (EUR):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.01"
                    value={monthlyGoal}
                    onChange={(e) => setMonthlyGoal(e.target.value)}
                    placeholder="e.g. 500.00"
                    className="settings-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="settings-help-text">
                  Target P&L per month
                </div>
              </div>

              <div className="settings-item">
                <label className="settings-label">
                  Monthly Drawdown Stop (%):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={monthlyDrawdownLimit}
                    onChange={(e) => setMonthlyDrawdownLimit(e.target.value)}
                    placeholder="e.g. 5"
                    className="settings-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="settings-help-text">
                  Telegram alert when the portfolio (IBKR, deposits/withdrawals excluded) drops this far from its high this month. Leave empty to disable. See the Risk page.
                </div>
              </div>

              <div className="settings-item">
                <label className="settings-label">
                  Yearly Goal Type:
                </label>
                <div className="settings-input-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="yearlyGoalType"
                      value="amount"
                      checked={yearlyGoalType === 'amount'}
                      onChange={(e) => setYearlyGoalType(e.target.value)}
                    />
                    <span>Absolute Amount (EUR)</span>
                  </label>
                  <label className="radio-label" style={{ marginLeft: '20px' }}>
                    <input
                      type="radio"
                      name="yearlyGoalType"
                      value="percentage"
                      checked={yearlyGoalType === 'percentage'}
                      onChange={(e) => setYearlyGoalType(e.target.value)}
                    />
                    <span>Percentage of Start Equity</span>
                  </label>
                </div>
                <div className="settings-help-text">
                  Choose whether to set your yearly goal as an absolute amount or as a percentage of your start equity
                </div>
              </div>

              {yearlyGoalType === 'amount' ? (
                <div className="settings-item">
                  <label className="settings-label">
                    Yearly P&L Goal (EUR):
                  </label>
                  <div className="settings-input-group">
                    <input
                      type="number"
                      step="0.01"
                      value={yearlyGoal}
                      onChange={(e) => setYearlyGoal(e.target.value)}
                      placeholder="e.g. 6000.00"
                      className="settings-input"
                      style={{ width: '200px' }}
                    />
                  </div>
                  <div className="settings-help-text">
                    Target P&L for the entire year as an absolute amount
                  </div>
                </div>
              ) : (
                <div className="settings-item">
                  <label className="settings-label">
                    Yearly P&L Goal (%):
                  </label>
                  <div className="settings-input-group">
                    <input
                      type="number"
                      step="0.01"
                      value={yearlyGoalPercentage}
                      onChange={(e) => setYearlyGoalPercentage(e.target.value)}
                      placeholder="e.g. 20.00"
                      className="settings-input"
                      style={{ width: '200px' }}
                    />
                    <span style={{ marginLeft: '10px', color: '#6c757d' }}>
                      % of start equity
                    </span>
                  </div>
                  <div className="settings-help-text">
                    Target P&L as a percentage of your start equity (e.g. 20.00 for 20% return)
                  </div>
                </div>
              )}

              <div className="settings-item">
                <label className="settings-label">
                  Year Start Equity (EUR):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.01"
                    value={yearlyStartEquity}
                    onChange={(e) => setYearlyStartEquity(e.target.value)}
                    placeholder="e.g. 10000.00"
                    className="settings-input"
                    style={{ width: '200px' }}
                  />
                  <button
                    className="btn-secondary"
                    onClick={handleAutoFillYearStartEquity}
                    style={{ marginLeft: '10px' }}
                  >
                    Auto-fill from Equity
                  </button>
                </div>
                <div className="settings-help-text">
                  Equity value on January 1st (required for percentage-based goals and year progress calculation). Click "Auto-fill" to get the equity value from January 1st if available.
                </div>
              </div>

              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e0e0e0' }}>
                <h3 style={{ marginBottom: '1rem', color: '#333' }}>Additional Yearly Goals (Optional)</h3>
                <div className="settings-help-text" style={{ marginBottom: '1.5rem' }}>
                  Set a minimum acceptable goal and a stretch/ambitious goal. These will be shown as additional lines in the year progress chart.
                </div>

                {/* Minimum Goal */}
                <div className="settings-item">
                  <label className="settings-label">
                    Minimum Goal Type:
                  </label>
                  <div className="settings-input-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="yearlyMinimumGoalType"
                        value="amount"
                        checked={yearlyMinimumGoalType === 'amount'}
                        onChange={(e) => setYearlyMinimumGoalType(e.target.value)}
                      />
                      <span>Absolute Amount (EUR)</span>
                    </label>
                    <label className="radio-label" style={{ marginLeft: '20px' }}>
                      <input
                        type="radio"
                        name="yearlyMinimumGoalType"
                        value="percentage"
                        checked={yearlyMinimumGoalType === 'percentage'}
                        onChange={(e) => setYearlyMinimumGoalType(e.target.value)}
                      />
                      <span>Percentage of Start Equity</span>
                    </label>
                  </div>
                </div>

                {yearlyMinimumGoalType === 'amount' ? (
                  <div className="settings-item">
                    <label className="settings-label">
                      Minimum Yearly Goal (EUR):
                    </label>
                    <div className="settings-input-group">
                      <input
                        type="number"
                        step="0.01"
                        value={yearlyMinimumGoal}
                        onChange={(e) => setYearlyMinimumGoal(e.target.value)}
                        placeholder="e.g. 3000.00"
                        className="settings-input"
                        style={{ width: '200px' }}
                      />
                    </div>
                    <div className="settings-help-text">
                      Minimum acceptable P&L for the year (should be lower than Target Goal)
                    </div>
                  </div>
                ) : (
                  <div className="settings-item">
                    <label className="settings-label">
                      Minimum Yearly Goal (%):
                    </label>
                    <div className="settings-input-group">
                      <input
                        type="number"
                        step="0.01"
                        value={yearlyMinimumGoalPercentage}
                        onChange={(e) => setYearlyMinimumGoalPercentage(e.target.value)}
                        placeholder="e.g. 10.00"
                        className="settings-input"
                        style={{ width: '200px' }}
                      />
                      <span style={{ marginLeft: '10px', color: '#6c757d' }}>
                        % of start equity
                      </span>
                    </div>
                    <div className="settings-help-text">
                      Minimum acceptable P&L as a percentage of start equity (should be lower than Target Goal)
                    </div>
                  </div>
                )}

                {/* Stretch Goal */}
                <div className="settings-item">
                  <label className="settings-label">
                    Stretch Goal Type:
                  </label>
                  <div className="settings-input-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="yearlyStretchGoalType"
                        value="amount"
                        checked={yearlyStretchGoalType === 'amount'}
                        onChange={(e) => setYearlyStretchGoalType(e.target.value)}
                      />
                      <span>Absolute Amount (EUR)</span>
                    </label>
                    <label className="radio-label" style={{ marginLeft: '20px' }}>
                      <input
                        type="radio"
                        name="yearlyStretchGoalType"
                        value="percentage"
                        checked={yearlyStretchGoalType === 'percentage'}
                        onChange={(e) => setYearlyStretchGoalType(e.target.value)}
                      />
                      <span>Percentage of Start Equity</span>
                    </label>
                  </div>
                </div>

                {yearlyStretchGoalType === 'amount' ? (
                  <div className="settings-item">
                    <label className="settings-label">
                      Stretch Yearly Goal (EUR):
                    </label>
                    <div className="settings-input-group">
                      <input
                        type="number"
                        step="0.01"
                        value={yearlyStretchGoal}
                        onChange={(e) => setYearlyStretchGoal(e.target.value)}
                        placeholder="e.g. 10000.00"
                        className="settings-input"
                        style={{ width: '200px' }}
                      />
                    </div>
                    <div className="settings-help-text">
                      Ambitious/high-performance P&L goal for the year (should be higher than Target Goal)
                    </div>
                  </div>
                ) : (
                  <div className="settings-item">
                    <label className="settings-label">
                      Stretch Yearly Goal (%):
                    </label>
                    <div className="settings-input-group">
                      <input
                        type="number"
                        step="0.01"
                        value={yearlyStretchGoalPercentage}
                        onChange={(e) => setYearlyStretchGoalPercentage(e.target.value)}
                        placeholder="e.g. 30.00"
                        className="settings-input"
                        style={{ width: '200px' }}
                      />
                      <span style={{ marginLeft: '10px', color: '#6c757d' }}>
                        % of start equity
                      </span>
                    </div>
                    <div className="settings-help-text">
                      Ambitious/high-performance P&L as a percentage of start equity (should be higher than Target Goal)
                    </div>
                  </div>
                )}
              </div>

              <div className="settings-item" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #dee2e6' }}>
                <label className="settings-label">
                  Equity Adjustment (EUR):
                </label>
                <div className="settings-input-group">
                  <input
                    type="number"
                    step="0.01"
                    value={equityAdjustment}
                    onChange={(e) => setEquityAdjustment(e.target.value)}
                    placeholder="e.g. 5000.00"
                    className="settings-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="settings-help-text">
                  Amount to deduct from current equity for goal calculations (e.g., total deposits made after January 1st). This ensures deposits don't affect your yearly profit and goal progress calculations.
                </div>
              </div>

              <div className="settings-item">
                <button
                  className="btn-save"
                  onClick={handleSaveGoals}
                  disabled={savingSettings}
                  style={{ marginTop: '1rem' }}
                >
                  {savingSettings ? 'Saving...' : 'Save Goals'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountDisplayNameManagement;

